import React, { useState, useEffect, useCallback } from 'react';
import { Search, Moon, Sun, Bookmark, ArrowLeft, Languages } from 'lucide-react';
import SearchBox from './components/SearchBox';
import SearchResults from './components/SearchResults';
import SkillSearchResults from './components/SkillSearchResults';
import SearchResultSkeleton from './components/SearchResultSkeleton';
import FavoritesModal from './components/FavoritesModal';
import TrendingCards from './components/TrendingCards'; // 显式导入防止tree-shaking
import TrendingSkills, { TRENDING_SKILLS } from './components/TrendingSkills';
import SearchTabSwitch, { SearchMode } from './components/SearchTabSwitch';
import { ToastContainer } from './components/Toast';
import { callLLM, RateLimitError } from './api/llmApi';
import { searchSkills } from './api/skillApi';
import { Skill } from './api/skillApi';
import { getRateLimitInfo } from './utils/rateLimiter';
import { Language, getTranslations, t } from './utils/i18n';
import CapturePage from './pages/spark/CapturePage';
import BoardPage from './pages/spark/BoardPage';
import SkillDetailPage from './pages/SkillDetailPage';
import PrivacyPage from './pages/PrivacyPage';

/**
 * 速率限制显示组件
 */
function RateLimitDisplay({ lang }: { lang: Language }) {
  const [rateInfo, setRateInfo] = useState(getRateLimitInfo());

  // 每秒更新一次（用于实时显示配额）
  useEffect(() => {
    const interval = setInterval(() => {
      setRateInfo(getRateLimitInfo());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const usagePercent = (rateInfo.todayCalls / 30) * 100;
  const isLowQuota = rateInfo.remainingCalls <= 5;

  return (
    <div className={`
      inline-flex items-center gap-3 px-4 py-2 rounded-lg text-sm
      ${isLowQuota
        ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
        : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
      }
    `}>
      <div className="flex items-center gap-2">
        <span className="font-medium">{t('dailyQuota', lang)}</span>
        <span className={`
          font-bold ${isLowQuota ? 'text-orange-600 dark:text-orange-400' : ''}
        `}>
          {rateInfo.remainingCalls}/30
        </span>
        <span className="text-xs opacity-70">{t('times', lang)}</span>
      </div>

      {/* 进度条 */}
      <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`
            h-full transition-all duration-300
            ${isLowQuota
              ? 'bg-orange-500'
              : usagePercent > 70
              ? 'bg-yellow-500'
              : 'bg-green-500'
            }
          `}
          style={{ width: `${usagePercent}%` }}
        />
      </div>

      {/* 缓存提示 */}
      <span className="text-xs opacity-70">
        {t('cacheTip', lang)}
      </span>
    </div>
  );
}

function App() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type?: 'success' | 'cache' }>>([]);
  const [lang, setLang] = useState<Language>(() => {
    // 从localStorage读取语言偏好，默认英文（避免合规问题）
    const savedLang = localStorage.getItem('psp_language') as Language;
    return savedLang || 'en';
  });

  // Spark页面路由
  const [currentPage, setCurrentPage] = useState<'home' | 'spark-capture' | 'spark-board' | 'privacy'>('home');

  // 搜索模式
  const [searchMode, setSearchMode] = useState<SearchMode>('tool');
  const [skillQuery, setSkillQuery] = useState('');
  const [skillResults, setSkillResults] = useState<any>(null);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [useLLMSearch, setUseLLMSearch] = useState(true); // LLM 搜索开关（默认开启）

  // 切换语言
  const toggleLanguage = () => {
    const newLang = lang === 'zh' ? 'en' : 'zh';
    setLang(newLang);
    localStorage.setItem('psp_language', newLang);
  };

  // 显示Toast通知
  const showToast = (message: string, type?: 'success' | 'cache') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // 处理工具搜索
  const handleSearch = async (query: string) => {
    if (!query.trim()) return;

    setIsLoading(true);
    setSearchQuery(query);
    setSkillQuery('');
    setSearchResults(null); // 清除旧结果，显示骨架屏
    setSkillResults(null);

    try {
      console.log("开始工具搜索:", query);
      const startTime = Date.now();
      const results = await callLLM(query);
      const searchTime = ((Date.now() - startTime) / 1000).toFixed(1);

      if (results) {
        setSearchResults(results);

        // 根据fromCache字段判断是否从缓存加载
        if (results.fromCache) {
          showToast(`⚡ 从缓存加载`, 'cache');
        } else {
          const rateInfo = getRateLimitInfo();
          showToast(`✅ 搜索完成（今日剩余 ${rateInfo.remainingCalls} 次API调用）`, 'success');
        }
      } else {
        alert("搜索失败，请稍后重试");
      }
    } catch (error) {
      // 处理速率限制错误
      if (error instanceof RateLimitError) {
        const rateInfo = getRateLimitInfo();
        showToast(
          `⚠️ ${error.message}（今日剩余: ${rateInfo.remainingCalls}次）`,
          'cache'
        );

        // 显示详细错误对话框
        alert(
          `⚠️ 速率限制\n\n` +
          `${error.message}\n\n` +
          `今日API调用统计：\n` +
          `• 已使用：${rateInfo.todayCalls}/${30}次\n` +
          `• 剩余：${rateInfo.remainingCalls}次\n\n` +
          `💡 缓存查询不受限制，已缓存工具可无限次使用`
        );
      } else {
        console.error("搜索失败:", error);
        alert("搜索失败: " + (error as Error).message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 处理 SKILL 搜索
  const handleSkillSearch = async (query: string) => {
    if (!query.trim()) return;

    setIsLoading(true);
    setSkillQuery(query);
    setSearchQuery('');
    setSkillResults(null);
    setSearchResults(null);

    try {
      const startTime = Date.now();
      const results = await searchSkills(query, lang, useLLMSearch);
      const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);

      if (results && results.results) {
        setSkillResults(results);

        // 根据fromCache字段判断是否从本地缓存加载
        if (results.fromCache) {
          const cacheMsg = lang === 'zh' ? '⚡ 缓存秒开' : '⚡ Loaded from cache';
          showToast(cacheMsg, 'cache');
        } else {
          const foundMsg = lang === 'zh'
            ? `📋 找到 ${results.resultCount} 个 MCP Skills`
            : `📋 Found ${results.resultCount} MCP Skills`;
          showToast(foundMsg, 'success');
        }
      } else {
        alert("搜索失败，请稍后重试");
      }
    } catch (error) {
      console.error("SKILL 搜索失败:", error);
      alert("搜索失败: " + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // 清除搜索
  const handleClearSearch = () => {
    setSearchQuery('');
    setSkillQuery('');
    setSearchResults(null);
    setSkillResults(null);
  };

  // 返回首页
  const handleBackToHome = () => {
    setSearchQuery('');
    setSkillQuery('');
    setSearchResults(null);
    setSkillResults(null);
    setSelectedSkill(null);
  };

  // 处理 SKILL 卡片点击 - 打开详情页
  const handleSkillCardClick = (skillName: string) => {

    // 1. 先从 TRENDING_SKILLS 查找
    let skill = TRENDING_SKILLS.find(s => s.name === skillName);

    // 2. 如果找不到，从当前搜索结果中查找
    if (!skill && skillResults && skillResults.results) {
      skill = skillResults.results.find((s: any) => s.name === skillName);
    }

    // 3. 如果还是找不到，触发一次搜索
    if (!skill) {
      handleSkillSearch(skillName);
      return;
    }

    // 4. 打开详情页
    setSelectedSkill(skill);
  };

  // 处理搜索结果中的 SKILL 点击
  const handleSkillSearchClick = async (skillName: string) => {
    await handleSkillSearch(skillName);
  };

  // 使用useCallback来稳定传递给子组件的函数引用，防止不必要的重新挂载
  const handleSparkBackToHome = useCallback(() => {
    setCurrentPage('home');
  }, []);

  const handleNavigateToBoard = useCallback(() => {
    setCurrentPage('spark-board');
  }, []);

  const handleNavigateToCapture = useCallback(() => {
    setCurrentPage('spark-capture');
  }, []);

  const handleNavigateToPrivacy = useCallback(() => {
    setCurrentPage('privacy');
  }, []);

  // 根据currentPage渲染不同页面 - 使用条件渲染而不是提前返回，避免组件重新挂载
  // SKILL 详情页优先级最高
  if (selectedSkill) {
    return (
      <div className={isDarkMode ? 'dark' : ''}>
        <SkillDetailPage
          skill={selectedSkill}
          lang={lang}
          onBack={() => setSelectedSkill(null)}
          onSkillClick={handleSkillCardClick}
          relatedSkills={TRENDING_SKILLS.filter(s => s.id !== selectedSkill.id).slice(0, 3)}
        />
      </div>
    );
  }

  if (currentPage === 'spark-capture') {
    return (
      <div className={isDarkMode ? 'dark' : ''}>
        <CapturePage
          onBackToHome={handleSparkBackToHome}
          onNavigateToBoard={handleNavigateToBoard}
          lang={lang}
        />
      </div>
    );
  }

  if (currentPage === 'spark-board') {
    return (
      <div className={isDarkMode ? 'dark' : ''}>
        <BoardPage
          onBackToHome={handleSparkBackToHome}
          onNavigateToCapture={handleNavigateToCapture}
          lang={lang}
        />
      </div>
    );
  }

  if (currentPage === 'privacy') {
    return (
      <div className={isDarkMode ? 'dark' : ''}>
        <PrivacyPage
          onBackToHome={handleSparkBackToHome}
          lang={lang}
        />
      </div>
    );
  }

  return (
    <div className={isDarkMode ? 'dark' : ''}>
      <div className="min-h-screen bg-[#F5F7FA] dark:bg-[#1E1E2E] transition-colors">
        {/* 顶部导航栏 */}
        <header className="bg-white dark:bg-[#2D2D3F] shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Logo */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#165DFF] rounded-lg flex items-center justify-center">
                  <Search className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-lg text-[#333647] dark:text-[#F5F7FA]">
                    Search what you can't remember
                  </h1>
                </div>
              </div>

              {/* 右侧操作 */}
              <div className="flex items-center gap-2 sm:gap-4">
                {/* Spark 火花系统 */}
                <div className="flex items-center gap-1 sm:gap-2">
                  <button
                    onClick={() => {
                      console.log('🔘 点击了捕获按钮，设置页面为 spark-capture');
                      setCurrentPage('spark-capture');
                    }}
                    className="flex items-center justify-center w-10 h-10 sm:w-auto sm:px-3 sm:py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                    title={t('sparkCapture', lang)}
                  >
                    <span>⚡</span>
                  </button>
                  <button
                    onClick={() => {
                      console.log('🔘 点击了随手记按钮，设置页面为 spark-board');
                      setCurrentPage('spark-board');
                    }}
                    className="flex items-center justify-center w-10 h-10 sm:w-auto sm:px-3 sm:py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                    title={t('sparkBoard', lang)}
                  >
                    <span>🎯</span>
                  </button>
                </div>

                {/* 语言切换 */}
                <button
                  onClick={toggleLanguage}
                  className="flex items-center justify-center w-10 h-10 sm:w-auto sm:px-3 sm:py-2 sm:gap-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Switch Language"
                  title={lang === 'zh' ? 'Switch to English' : '切换到中文'}
                >
                  <Languages className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>

                {/* 主题切换 */}
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="flex items-center justify-center w-10 h-10 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="切换主题"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600" />
                  )}
                </button>

                {/* 收藏工具 */}
                <button
                  onClick={() => setShowFavorites(true)}
                  className="flex items-center justify-center w-10 h-10 sm:w-auto sm:px-4 sm:py-2 sm:gap-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="收藏工具"
                >
                  <Bookmark className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* 主内容区 */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* 搜索框区域 */}
          <div className="mb-12">
            {/* 核心Slogan - 仅在首页显示 */}
            {!searchQuery && !skillQuery && !searchResults && !skillResults && (
              <div className="text-center mb-8">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-3">
                  {lang === 'zh' ? '懂你的智能搜索' : 'Smart Search That Gets You'}
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  {lang === 'zh' ? '不需要背工具名词' : "No need to memorize tool names"}
                </p>
              </div>
            )}

            {/* Tab 切换 - 仅在首页显示 */}
            {!searchQuery && !skillQuery && !searchResults && !skillResults && (
              <SearchTabSwitch
                currentMode={searchMode}
                onModeChange={(mode) => {
                  setSearchMode(mode);
                  console.log('切换搜索模式:', mode);
                }}
                lang={lang}
              />
            )}

            <SearchBox
              onSearch={searchMode === 'tool' ? handleSearch : handleSkillSearch}
              isLoading={isLoading}
              hasResults={!!(searchResults || skillResults)}
              onClear={handleClearSearch}
              lang={lang}
              mode={searchMode}
            />
          </div>

          {/* 返回按钮（当有搜索结果时） */}
          {(searchQuery || skillQuery) && (
            <div className="mb-6">
              <button
                onClick={handleBackToHome}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('backToHome', lang)}
              </button>
            </div>
          )}

          {/* 热门列表 - 仅在首页显示，根据模式切换 */}
          {!searchQuery && !skillQuery && !searchResults && !skillResults && (
            <div className="mt-16">
              {searchMode === 'tool' ? (
                <TrendingCards
                  onRepoClick={(repoName) => {
                    handleSearch(repoName);
                  }}
                  lang={lang}
                />
              ) : (
                <TrendingSkills
                  onSkillClick={handleSkillCardClick}
                  lang={lang}
                />
              )}
            </div>
          )}

          {/* 内容显示逻辑 */}
          {isLoading ? (
            // 加载中显示骨架屏
            <SearchResultSkeleton />
          ) : searchResults ? (
            // 工具搜索结果页面
            <SearchResults results={searchResults} query={searchQuery} lang={lang} onSearch={handleSearch} />
          ) : skillResults ? (
            // SKILL 搜索结果页面
            <SkillSearchResults
              result={skillResults}
              query={skillQuery}
              lang={lang}
              onSkillClick={handleSkillCardClick}
              useLLM={useLLMSearch}
              onToggleLLM={() => setUseLLMSearch(!useLLMSearch)}
            />
          ) : null}

          {/* 页脚广告位 */}
          {!searchResults && !skillResults && (
            <div className="mt-16">
              <a
                href="https://douya.world"
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-8 text-center border border-gray-200 dark:border-gray-600 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                  <div className="text-4xl mb-4">🌊</div>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-3">
                    {lang === 'zh' ? '遇见那些从未被书写过的思想' : 'Meet Thoughts Never Written Before'}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                    douya.world — {lang === 'zh' ? '三个孩童上帝的对话片段' : 'Dialogue fragments from three child gods'}
                  </p>
                  <div className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#165DFF] text-white rounded-lg group-hover:bg-[#0E4FD0] transition-colors font-medium">
                    {lang === 'zh' ? '踏入圣殿' : 'Enter the Sanctuary'}
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </div>
              </a>
            </div>
          )}
        </main>

        {/* 页脚 */}
        {!searchResults && !skillResults && (
          <footer className="bg-white dark:bg-[#2D2D3F] border-t border-gray-200 dark:border-gray-700 mt-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              {/* API配额显示 */}
              <div className="mb-4 text-center">
                <RateLimitDisplay lang={lang} />
              </div>
              <div className="text-center text-sm text-gray-600 dark:text-gray-400 space-y-2">
                <p>{t('source', lang)} | {t('lastUpdate', lang)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-500 italic">
                  {lang === 'zh' ? '把复杂留给AI，把简单留给自己' : 'Leave complexity to AI, keep simplicity for yourself'}
                </p>
                <div className="flex justify-center gap-6">
                  <a href="#" className="hover:text-[#165DFF] transition-colors">{t('aboutUs', lang)}</a>
                  <button
                    onClick={handleNavigateToPrivacy}
                    className="hover:text-[#165DFF] transition-colors bg-transparent border-0 cursor-pointer text-sm text-gray-600 dark:text-gray-400"
                  >
                    {t('privacyPolicy', lang)}
                  </button>
                  <a href="mailto:douyaworld2026@163.com" className="hover:text-[#165DFF] transition-colors">{t('advertising', lang)}</a>
                </div>
              </div>
            </div>
          </footer>
        )}
      </div>

      {/* 收藏夹弹窗 */}
      {showFavorites && (
        <FavoritesModal onClose={() => setShowFavorites(false)} lang={lang} />
      )}

      {/* Toast通知容器 */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}

export default App;
